import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  CreateIssuePayload,
  IssueComment,
  IssueDetail,
  IssueListItem,
  IssueProduct,
  PaginatedResponse,
  ProductModule,
  UpdateIssuePayload,
} from "../types";

const ISSUES_KEY = ["issues"] as const;

export function useIssueList(
  params: {
    page?: number;
    limit?: number;
    search?: string;
    productId?: string;
    moduleId?: string;
    submoduleId?: string;
    category?: string;
    priority?: string;
    status?: string;
    assigneeId?: string;
    reportedById?: string;
    enabled?: boolean;
  } = {},
): UseQueryResult<PaginatedResponse<IssueListItem>> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...ISSUES_KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && params.enabled !== false,
    queryFn: () =>
      apiFetch<PaginatedResponse<IssueListItem>>("/issues", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          productId: params.productId,
          moduleId: params.moduleId,
          submoduleId: params.submoduleId,
          category: params.category,
          priority: params.priority,
          status: params.status,
          assigneeId: params.assigneeId,
          reportedById: params.reportedById,
        },
      }),
  });
}

export function useIssue(id: string): UseQueryResult<IssueDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...ISSUES_KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ issue: IssueDetail }>(`/issues/${id}`, {
        token: accessToken,
      });
      return res.issue;
    },
  });
}

/** Access-filtered product list for the issue form. */
export function useIssueProducts(): UseQueryResult<IssueProduct[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...ISSUES_KEY, "products", accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: async () => {
      const res = await apiFetch<{ products: IssueProduct[] }>(
        "/issues/products",
        { token: accessToken },
      );
      return res.products;
    },
  });
}

/** Modules (with submodules) for a product — drives the dependent dropdowns. */
export function useProductModules(
  productId: string | null | undefined,
): UseQueryResult<ProductModule[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: ["product-modules", productId, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!productId,
    queryFn: async () => {
      const res = await apiFetch<{ modules: ProductModule[] }>(
        `/products/${productId}/modules`,
        { token: accessToken },
      );
      return res.modules;
    },
  });
}

export function useCreateIssue() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateIssuePayload) =>
      apiFetch<{ issue: IssueDetail }>("/issues", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ISSUES_KEY] });
    },
  });
}

export function useUpdateIssue(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateIssuePayload) =>
      apiFetch<{ issue: IssueDetail }>(`/issues/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ISSUES_KEY] });
    },
  });
}

export function useDeleteIssue() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/issues/${id}`, { method: "DELETE", token: accessToken }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ISSUES_KEY] });
    },
  });
}

// ── Comments ────────────────────────────────────────────

export function useCreateComment(issueId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { content: string; parentId?: string | null }) =>
      apiFetch<{ comment: IssueComment }>(`/issues/${issueId}/comments`, {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ISSUES_KEY, "detail", issueId] });
    },
  });
}

export function useEditComment(issueId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) =>
      apiFetch<{ comment: IssueComment }>(`/issue-comments/${id}`, {
        method: "PATCH",
        body: { content },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ISSUES_KEY, "detail", issueId] });
    },
  });
}

export function useDeleteComment(issueId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/issue-comments/${id}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ISSUES_KEY, "detail", issueId] });
    },
  });
}
